<script setup>
import { useHead, useNuxtApp, useRoute, useSeoMeta } from '#imports';
import { useStore } from 'vuex';
import { computed } from 'vue';
import AuthTimer from '~/components/AuthTimer/AuthTimer.vue';
import BackToTop from '~/components/BackToTop/BackToTop.vue';
import getThemeConfig from '~/utils/getThemeConfig';
import { isParticipantFacingRoute } from '~/utils/participantRoute';
import { resolveParticipantPortalLocale } from '~/utils/participantUiLocale';
import {
  resolveParticipantHtmlLang,
  resolveParticipantTextDirection,
} from '~/utils/participantTextDirection';

const route = useRoute();
const store = useStore();
const { $auth } = useNuxtApp();

// ------------------------------
// COMMUNITY NAME (from Vuex)
// ------------------------------
const communityName = computed(() => store.getters['participant/communityName']);

const taskName = computed(() => store.state.participant?.taskState?.data?.task?.name);

// ------------------------------
// DYNAMIC TITLE LOGIC
// ------------------------------
const pageTitle = computed(() => {
  const name = route.name || '';

  // ------------------------------
  // ADMIN ROUTES
  // ------------------------------
  if (name.startsWith('theme-admin')) {
    return 'Admin Dashboard – Whycatcher';
  }

  // ------------------------------
  // PARTICIPANT TASK ROUTES
  // e.g. theme-participant-task-slug
  // ------------------------------
  if (name.startsWith('theme-participant-task')) {
    // Prefer task name → community name → fallback
    if (taskName.value) {
      return `${taskName.value} – Whycatcher`;
    }
    if (communityName.value) {
      return communityName.value;
    }
    return 'Your Dashboard – Whycatcher';
  }

  // ------------------------------
  // GENERAL PARTICIPANT ROUTES
  // ------------------------------
  if (name.startsWith('theme-participant')) {
    return communityName.value || 'Your Dashboard – Whycatcher';
  }

  // ------------------------------
  // DEFAULT
  // ------------------------------
  return 'Whycatcher';
});

const participantRouteLocale = computed(() => {
  if (!isParticipantFacingRoute(route)) {
    return null;
  }

  return resolveParticipantPortalLocale({
    user: $auth.user,
    query: route.query,
  });
});

const participantDocumentDirection = computed(() => (
  participantRouteLocale.value
    ? resolveParticipantTextDirection(participantRouteLocale.value)
    : null
));

const participantHtmlLang = computed(() => (
  participantRouteLocale.value
    ? resolveParticipantHtmlLang(participantRouteLocale.value)
    : null
));

useSeoMeta({
  title: () => pageTitle.value,
  description: 'Whycatcher research platform'
});

// ------------------------------
// COOKIEBOT LOGIC
// ------------------------------
const isAuthPage = route.name === 'email-login';
const isLoginPage = route.name === 'login';
const hostname = window.location.hostname;
const host = window.location.host;

const isLocalhost = hostname.includes('localhost') || hostname.includes('whycatcher.internal');
const isDev = host.includes('dev-') || host.includes('self-');

let cbid = null;
if (!isAuthPage && !isLoginPage && !isLocalhost && !isDev) {
  const theme = getThemeConfig();
  cbid = theme?.config?.cookieBotId;
}

if (cbid) {
  useHead({
    script: [
      {
        id: 'Cookiebot',
        src: 'https://consent.cookiebot.com/uc.js',
        type: 'text/javascript',
        'data-cbid': cbid,
        'data-blockingmode': 'auto'
      }
    ]
  });
}
</script>

<template>
  <div
    class="content-pane"
    data-v-sticky-container
    :dir="participantDocumentDirection || undefined"
    :lang="participantHtmlLang || undefined"
  >
    <AuthTimer />
    <BackToTop />
    <div :class="$auth?.user?.isAdmin && 'admin-layout'">
      <slot />
    </div>
  </div>
</template>
